import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import type { PermissionDefinition } from '@/lib/permissions';

interface UsePermissionsReturn {
  /** All available permission definitions (from DB). */
  definitions: PermissionDefinition[];
  /** The admin's assigned permission keys. Empty = superadmin. */
  permissions: string[];
  /** True if the admin has no restrictions (zero entries). */
  isSuperAdmin: boolean;
  /** Check if the admin can access a specific feature by key. */
  hasAccess: (key: string) => boolean;
  /**
   * Check if the current dashboard path is allowed.
   * For superadmins: always true.
   * For restricted admins: true only if the path matches a route
   * in one of their assigned permission definitions.
   */
  canAccessRoute: (path: string) => boolean;
  /** The first allowed route for this admin (for redirects). */
  defaultRoute: string;
  /** True while permissions are being fetched. */
  isLoading: boolean;
}

export function usePermissions(): UsePermissionsReturn {
  const { adminId, isAuthorized } = useAuth();

  // Fetch all permission definitions (shared across all admins)
  const { data: definitions = [], isLoading: defsLoading } = useQuery({
    queryKey: ['permission-definitions'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('admin_permission_definitions')
        .select('id, key, label, routes')
        .order('label');
      if (error) throw error;
      return (data || []) as PermissionDefinition[];
    },
    enabled: isAuthorized,
    staleTime: 10 * 60 * 1000,
  });

  // Fetch this admin's assigned permissions (join to get key + routes)
  const { data: assignedDefs = [], isLoading: permsLoading } = useQuery({
    queryKey: ['admin-permissions', adminId],
    queryFn: async () => {
      if (!adminId) return [];
      const { data, error } = await supabase
        .from('admin_permissions')
        .select('admin_permission_definitions(key, routes)')
        .eq('admin_id', adminId);
      if (error) throw error;
      return (data || [])
        .map((r: any) => r.admin_permission_definitions)
        .filter(Boolean) as { key: string; routes: string[] }[];
    },
    enabled: !!adminId && isAuthorized,
    staleTime: 5 * 60 * 1000,
  });

  const isLoading = defsLoading || permsLoading;
  const permissions = useMemo(() => assignedDefs.map((d) => d.key), [assignedDefs]);
  const isSuperAdmin = !isLoading && permissions.length === 0;

  // Build a set of allowed route segments for fast lookup
  const allowedRoutes = useMemo(() => {
    return new Set(assignedDefs.flatMap((d) => d.routes));
  }, [assignedDefs]);

  const hasAccess = useCallback((key: string): boolean => {
    if (isLoading) return false;
    if (permissions.length === 0) return true; // superadmin
    return permissions.includes(key);
  }, [isLoading, permissions]);

  const canAccessRoute = useCallback((path: string): boolean => {
    if (isLoading) return false;
    if (permissions.length === 0) return true; // superadmin

    // Extract the first segment after /dashboard/
    const segment = path.replace(/^\/dashboard\/?/, '').split('/')[0] || '';

    // Empty segment means /dashboard itself — restricted admins can't access it
    if (segment === '') return false;

    return allowedRoutes.has(segment);
  }, [isLoading, permissions, allowedRoutes]);

  // First allowed route for redirects
  const defaultRoute = useMemo(() => {
    if (permissions.length === 0) return '/dashboard';
    const firstRoute = assignedDefs[0]?.routes[0];
    return firstRoute ? `/dashboard/${firstRoute}` : '/dashboard';
  }, [permissions, assignedDefs]);

  return { definitions, permissions, isSuperAdmin, hasAccess, canAccessRoute, defaultRoute, isLoading };
}
